import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Settings,
  Moon,
  Sun,
  HelpCircle,
  Shield,
  FileText,
  Info,
  LogOut,
  ChevronRight,
  Monitor,
  Lock,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useTheme } from "@/hooks/use-theme";
import { useToast } from "@/hooks/use-toast";
import { useOuvidoriaStore } from "@/stores/ouvidoriaStore";
import { autenticacaoService } from "@/services/autenticacaoService";
import { privacidadeService } from "@/services/privacidadeService";

interface SettingsItemProps {
  icon: React.ElementType;
  title: string;
  description?: string;
  onClick?: () => void;
  href?: string;
  action?: React.ReactNode;
  external?: boolean;
}

function SettingsItem({ icon: Icon, title, description, onClick, href, action, external }: SettingsItemProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (href) {
      if (external) {
        window.open(href, "_blank");
      } else {
        navigate(href);
      }
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-accent/50 transition-colors text-left group"
    >
      <div className="flex items-center gap-4">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="font-medium text-foreground">{title}</p>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      {action || (
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
      )}
    </button>
  );
}

export default function ConfiguracoesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const { logout } = useOuvidoriaStore();
  const [sobreNosOpen, setSobreNosOpen] = useState(false);
  const [privacidadeOpen, setPrivacidadeOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [descricao, setDescricao] = useState("");
  const [enviando, setEnviando] = useState(false);

  const handleLogout = () => {
    logout();
    autenticacaoService.removerToken();
    toast({
      title: "Sessão encerrada",
      description: "Você saiu da sua conta.",
    });
    navigate("/login");
  };

  const handleSolicitarDireitos = async () => {
    setEnviando(true);
    const result = await privacidadeService.solicitarMeusDireitos({
      email,
      descricao,
    });
    setEnviando(false);

    if ("failure" in result) {
      toast({
        title: "Erro ao enviar solicitação",
        description: result.failure.messages?.[0] || "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Solicitação enviada",
      description: "Seu pedido de direitos foi registrado com sucesso.",
    });
    setEmail("");
    setDescricao("");
    setPrivacidadeOpen(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-3xl mx-auto space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <h1 className="font-heading text-2xl font-bold">Configurações</h1>
        <p className="text-muted-foreground mt-1">
          Gerencie suas preferências e informações da conta
        </p>
      </motion.div>

      {/* Aparência */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Monitor className="h-5 w-5" />
              Aparência
            </CardTitle>
            <CardDescription>
              Personalize a interface do sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-accent/30">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  {theme === "dark" ? (
                    <Moon className="h-5 w-5 text-primary" />
                  ) : (
                    <Sun className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-medium">Tema {theme === "dark" ? "escuro" : "claro"}</p>
                  <p className="text-sm text-muted-foreground">
                    Alterne entre modo claro e escuro
                  </p>
                </div>
              </div>
              <Switch
                id="theme-toggle"
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Privacidade */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-5 w-5" />
              Privacidade
            </CardTitle>
            <CardDescription>
              Gerencie seus dados e exercite seus direitos
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <SettingsItem
              icon={Lock}
              title="Meus Direitos"
              description="Solicite acesso, correção, exclusão ou portabilidade dos seus dados"
              onClick={() => setPrivacidadeOpen(true)}
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Suporte e Informações */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HelpCircle className="h-5 w-5" />
              Suporte e Informações
            </CardTitle>
            <CardDescription>
              Acesse ajuda, termos e políticas da plataforma
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <SettingsItem
              icon={HelpCircle}
              title="Central de Ajuda"
              description="Tire dúvidas e aprenda a usar o sistema"
              href="/reportar-problema"
            />
            <Separator />
            <SettingsItem
              icon={Shield}
              title="Política de Privacidade"
              description="Saiba como seus dados são tratados"
              href="/politica-privacidade"
            />
            <Separator />
            <SettingsItem
              icon={FileText}
              title="Termos de Uso"
              description="Leia os termos e condições da plataforma"
              href="/termos-uso"
            />
            <Separator />
            <SettingsItem
              icon={Info}
              title="Sobre Nós"
              description="Conheça a UrbNet e nossa missão"
              onClick={() => setSobreNosOpen(true)}
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Conta */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings className="h-5 w-5" />
              Conta
            </CardTitle>
            <CardDescription>
              Gerencie sua sessão e acesso
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <SettingsItem
              icon={LogOut}
              title="Sair da conta"
              description="Encerre sua sessão atual"
              onClick={handleLogout}
              action={
                <Button variant="destructive" size="sm" className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Sair
                </Button>
              }
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Sobre Nós */}
      <Dialog open={sobreNosOpen} onOpenChange={setSobreNosOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Sobre Nós
            </DialogTitle>
            <DialogDescription>Conheça a UrbNet e nossa missão</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 leading-relaxed text-sm text-muted-foreground">
            <h3 className="font-semibold text-foreground text-base">Quem Somos</h3>
            <p>
              A Marelli Desenvolvimentos Eireli, com sede em Cachoeiro de Itapemirim, Espírito Santo, nasceu de uma constatação simples: a maioria das cidades brasileiras enfrenta os mesmos problemas urbanos há décadas — não por falta de vontade da gestão pública, mas por falta de uma infraestrutura digital capaz de transformar reclamação em ação rastreável.
            </p>
            <p>
              O UrbNet é a resposta a essa lacuna. Somos uma plataforma gov-tech construída para conectar cidadãos, ouvidorias municipais e gestores públicos em um único ecossistema — com Inteligência Artificial validando evidências em tempo real, hierarquia institucional verificada, SLA público e auditabilidade completa para os órgãos de controle.
            </p>
            <p>
              Não somos uma adaptação de produto estrangeiro, nem uma cópia do que já existe no mercado brasileiro. O UrbNet foi desenhado do zero, pensando na realidade concreta das prefeituras — de municípios de 20 mil habitantes a capitais — e na necessidade real de transparência que os Tribunais de Contas estaduais estão exigindo com força crescente.
            </p>
            <p>
              Acreditamos que tecnologia urbana não deve ser privilégio de metrópoles com orçamento robusto. Por isso, construímos uma plataforma escalável, com planos que respeitam o porte de cada município, e uma arquitetura de dados que protege o cidadão — que usa o app de forma anônima — e ao mesmo tempo entrega ao gestor público a inteligência que ele precisa para agir.
            </p>
            <p>
              O UrbNet é, acima de tudo, uma aposta de que gestão urbana eficiente e transparência pública não são metas distantes — são o próximo passo natural de qualquer prefeitura que queira estar à frente do seu tempo.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meus Direitos */}
      <Dialog open={privacidadeOpen} onOpenChange={setPrivacidadeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Meus Direitos
            </DialogTitle>
            <DialogDescription>
              Exercite seus direitos sobre os dados pessoais (acesso, correção, exclusão, portabilidade etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-direitos">E-mail</Label>
              <Input
                id="email-direitos"
                type="email"
                placeholder="Informe seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                E-mail vinculado à sua conta para validar a solicitação.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao-direitos">Descrição da solicitação</Label>
              <Textarea
                id="descricao-direitos"
                placeholder="Descreva o direito que deseja exercer (acesso, correção, exclusão, portabilidade...)"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
              />
            </div>
            <Button
              onClick={handleSolicitarDireitos}
              disabled={enviando || !email.trim() || !descricao.trim()}
              className="w-full"
            >
              {enviando ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar solicitação"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
